<template>
  <ValidationProvider
    v-slot="{ errors }"
    :vid="vid"
    :name="name"
    :rules="rules"
    tag="div"
    ref="provider"
  >
    <AGCLabel v-if="label" :required="required" :id="id">
      <slot>{{ label }}</slot>
      <template v-slot:subLabel>
        <slot name="subLabel">{{ subLabel }}</slot>
      </template>
    </AGCLabel>
    <div>
      <vue-dropzone
        v-if="!readOnly"
        :id="dropzoneId"
        :options="dropzoneOptions"
        :useCustomSlot="true"
        ref="dropzone"
        @vdropzone-files-added="handleAdded"
        @vdropzone-success="onFileSuccess"
        @vdropzone-success-multiple="onMultipleFileSuccess"
        @vdropzone-error="handleError"
        @vdropzone-complete="handleComplete"
        @vdropzone-complete-multiple="handleCompleteMultiple"
      >
        <div class="dropzone-custom-content">
          <h6 v-if="dropzoneTitle">{{ dropzoneTitle }}</h6>
          <img
            src="../../assets/img/icon/cloud-upload.png"
            alt="Cloud upload Icon"
            class="img-upload"
          />
          <h3 class="dropzone-custom-title" v-show="!inputVal || !inputVal.length">
            拖放檔案到這裡或
          </h3>
          <div class="subtitle">
            <button type="button" class="btn-upload-image">選取檔案</button>
          </div>
        </div>
      </vue-dropzone>
      <div class="file-uploader__error-list">
        <span v-if="errors" class="file-uploader__error">{{ errors[0] }}</span>
        <span
          v-for="(errorMsg, filename) in customErrors"
          :key="filename"
          class="file-uploader__error"
        >
          {{ (filename ? filename + " - " : "") + errorMsg }}
        </span>
      </div>
      <div class="dropzone__hint">
        <slot name="hint"></slot>
      </div>
      <ThumbnailPreviewList
        v-model="inputVal"
        v-stream:remove-click="onRemoveClick$"
        :multiple="multiple"
        :upload-type="uploadType"
        :readOnly="readOnly"
        :t="t"
        :role="role"
        :dropzoneTitle="dropzoneTitle"
        @edit="handleEdit"
        @download="downloadFile"
      ></ThumbnailPreviewList>
    </div>
    <CropperModal
      v-model="cropperModal"
      :upload-type="uploadType"
      :public-api="publicApi"
      @cropped="reloadImages"
    ></CropperModal>
  </ValidationProvider>
</template>

<script>
import { tap, pluck } from 'rxjs/operators';
import { mapGetters } from 'vuex';
import vue2Dropzone from 'vue2-dropzone';
import store, { REQUIRE_LOGIN } from '@/store';
import imagePath from '@/filters/image-path';

import ThumbnailPreviewList from '@/components/thumbnail/ThumbnailPreviewList.vue';
import CropperModal from '@/components/image/CropperModal.vue';
import agcFormElementMixin from '../../mixins/agc-form-element';

const fileInfo = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onloadend = (ev) => {
    resolve({
      filename: file.name,
      dataUrl: ev.target.result,
    });
  };
  reader.readAsDataURL(file);
});

const aspectRatioOfImageSrc = (imageSrc) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    resolve(img.width / img.height);
  };
  img.src = imageSrc;
});

export default {
  mixins: [agcFormElementMixin],
  components: {
    vueDropzone: vue2Dropzone,
    ThumbnailPreviewList,
    CropperModal,
  },
  props: {
    maxFiles: {
      type: Number,
      default: 1,
    },
    publicApi: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    uploadType: String,
    acceptedFiles: {
      type: String,
      default: '.jpg,.jpeg,.png,.gif',
    },
    acceptedAspectRatios: Array, // e.g. ['16:9', '1:1']
    filters: {
      imagePath,
    },
    dropzoneTitle: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    cropperModal: {
      show: false,
      image: null,
    },
    t: null,
    customErrors: {}, // customErrors[filename] = errorMsg
  }),
  domStreams: ['onRemoveClick$'],
  subscriptions() {
    return {
      onRemoveClick: this.onRemoveClick$.pipe(
        pluck('event', 'msg'),
        tap((imageNameToRemove) => {
          if (this.multiple) {
            this.inputVal = this.inputVal.filter((imageName) => imageName !== imageNameToRemove);
          } else {
            this.inputVal = null;
          }
          this.customErrors = {};
        }),
      ),
    };
  },
  computed: {
    ...mapGetters(['role']),
    multiple() {
      return this.maxFiles > 1;
    },
    apiBase() {
      return this.publicApi ? 'public' : this.role;
    },
    uploadUrl() {
      const uri = this.multiple ? 'images' : 'image';
      return `/agc-${this.apiBase}-api/upload/${uri}?type=${this.uploadType}`;
    },
    dropzoneId() {
      return `df-${this.id}`;
    },
    dropzoneOptions() {
      return {
        uploadMultiple: this.multiple,
        url: this.uploadUrl,
        paramName: 'upload',
        parallelUploads: this.maxFiles,
        thumbnailWidth: 150,
        thumbnailHeight: 150,
        thumbnailMethod: 'contain',
        maxFiles: this.maxFiles,
        maxFilesize: 2,
        acceptedFiles: this.acceptedFiles,
        dictFileTooBig: '檔案大小不能多於{{maxFilesize}}MB',
        dictInvalidFileType: '請上傳正確檔案類型',
        dictCancelUpload: '取消上傳',
        dictCancelUploadConfirmation: '確定移除圖片嗎？',
        addRemoveLinks: true,
        dictRemoveFile: '刪除',
        autoQueue: false,
      };
    },
  },
  methods: {
    async handleAdded(files) {
      this.customErrors = {};

      // need $nextTick for remove file APIs to work properly
      this.$nextTick(async () => {
        // manually check maxFiles
        if ((this.inputVal?.length ?? 0) + files.length > this.maxFiles) {
          this.$set(this.customErrors, '', `最多只能上傳 ${this.maxFiles} 張`);
          this.$refs.dropzone.removeAllFiles();
        }

        // filter out files that does not match any of the accepted aspect ratios
        if (this.acceptedAspectRatios) {
          const acceptedAspectRatios = this.acceptedAspectRatios.map((ratioStr) => {
            const [w, h] = ratioStr.split(':');
            return [w, h];
          });
          await Promise.all(
            [...files].map(async (file) => {
              const { filename, dataUrl } = await fileInfo(file);
              const aspectRatio = await aspectRatioOfImageSrc(dataUrl);

              // remove file if all aspect ratios do not match
              if (acceptedAspectRatios.every(([w, h]) => Math.abs(aspectRatio - w / h) > 0)) {
                this.$refs.dropzone.removeFile(file);
                this.$set(
                  this.customErrors,
                  filename,
                  `只可上傳 ${this.acceptedAspectRatios.join(' 或 ')} 圖片`,
                );
              }
            }),
          );
        }

        this.$refs.dropzone.getAcceptedFiles().forEach((file) => {
          this.$refs.dropzone.dropzone.enqueueFile(file);
        });
      });
    },
    onFileSuccess(_, { payload }) {
      if (!this.multiple) {
        this.inputVal = payload;
      }
    },
    onMultipleFileSuccess(_, { payload }) {
      this.inputVal = this.inputVal.concat(payload);
    },
    handleEdit(image) {
      this.cropperModal = {
        show: true,
        image,
      };
    },
    downloadFile(image) {
      const url = imagePath(image, this.uploadType, 'original', true, this.t);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', image);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    reloadImages() {
      this.t = new Date().getTime();
    },
    handleError({ name: filename }, msg) {
      if (msg.msg === 'UNAUTHORIZED') {
        store.dispatch(REQUIRE_LOGIN);
        return;
      }
      if (msg === 'You can not upload any more files.') {
        this.$set(this.customErrors, '', `最多只能上傳 ${this.maxFiles} 張`);
      } else {
        this.$nextTick(() => {
          this.$set(this.customErrors, filename, msg);
        });
      }
    },
    handleComplete() {
      if (!this.multiple) {
        this.$refs.dropzone.removeAllFiles();
      }
    },
    handleCompleteMultiple() {
      this.$refs.dropzone.removeAllFiles();
    },
  },
};
</script>

<style>
@import '~vue2-dropzone/dist/vue2Dropzone.min.css';
</style>

<style scoped lang="scss">
.file-uploader {
  &__error-list {
    display: flex;
    flex-direction: column;
  }

  &__error {
    font-size: 80%;
    color: #dc3545;
  }
}

.dropzone {
  font-family: inherit;
  background: #f9f9fa;
  text-align: center;
  vertical-align: middle;
  padding: 15px;
  min-height: unset;
  border: 2px dashed #CED4D9;
  border-radius: 4px;
  background-color: #FFFFFF;

  &-custom-title {
    font-size: 14px;
    font-weight: 400;
    padding: 6px 0px;
    color: #aaa;
  }

  .img-upload {
    width: 50px;
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .subtitle {
    .btn-upload-image {
      background-color: #e0effd;
      border: 1px solid #b1d7fb;
      font-size: 14px;
      color: #318ce1;
      border-radius: 4px;
      padding: 6px 16px;
      margin-bottom: 16px;
    }
  }

  &__hint {
    margin-top: 4px;
    color: #7e8b97;
    font-size: 12px;
  }

  ::v-deep {
    .dz-message {
      margin: 0;
    }

    .dz-preview {
      .dz-error-message {
        top: 50%;
      }
      .dz-error-mark {
        top: 22%;
        display: none;
      }
      .dz-image {
        img {
          height: 9rem;
          width: 9rem;
          object-fit: cover;
        }
      }
      &.dz-error.dz-complete {
         .dz-remove {
           left: 32%;
         }
      }
      .dz-remove {
        margin: 0px;
        top: 15%;
        bottom: initial;
        left: 22%;

        &:hover {
          text-decoration: none;
        }
      }
    }

    .dz-progress {
      top: 80%;
    }

    .dz-details {
      .dz-size,
      .dz-filename {
        display: none;
      }
    }
  }
}
</style>
